<template>
  <div class="main">
    <b-card>
      <b-card-body>
        <b-row class="mb-2">
          <b-col md="12" class="d-flex justify-content-end">
            <b-button variant="primary" @click="showAddMetricModal">
              <feather-icon icon="PlusIcon" class="mr-50" />
              New Metric
            </b-button>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="2" sm="4" class="my-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">Per page</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
                class="w-50"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            class="d-flex justify-content-end mb-2"
            style="position: relative; z-index: 1"
          >
            <b-button variant="outline-danger" size="sm" @click="clearFilters">
              Clear Filters
            </b-button>
          </b-col>

          <b-col cols="12">
            <b-table
              striped
              hover
              responsive
              :per-page="perPage"
              :current-page="currentPage"
              :items="metricslist"
              :fields="metrics_fields"
              @filtered="onFiltered"
            >
              <template #cell(date)="data">
                {{ formatDateDDMMMYYYY(data.item.date) }}
              </template>

              <template #cell(actions)="data">
                <b-dropdown
                  variant="link"
                  toggle-class="p-0"
                  no-caret
                  class="m-1"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>

                  <b-dropdown-item @click="showEditMetricModal(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-1">Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="confirmDeleteMetric(data.item.id)">
                    <feather-icon icon="TrashIcon" />
                    <span class="align-middle ml-1">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <div class="d-flex justify-content-center mb-1">
              <div class="d-flex justify-content-center mb-1" v-if="loading">
                <b-spinner
                  variant="success"
                  label="Spinning"
                  style="width: 3rem; height: 3rem"
                ></b-spinner>
              </div>
            </div>
          </b-col>

          <b-col cols="12">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="my-0"
            />
            <b-form-group>
              Showing
              {{ totalRows > 0 ? perPage * (currentPage - 1) + 1 : 0 }}
              to
              {{
                totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0
              }}
              of
              {{ totalRows > 0 ? totalRows : 0 }}
              entries
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Modal for adding a new Metric-->
        <b-modal
          ref="modalAddMetric"
          id="modal-add-metric"
          title="Add New Metric"
          @hide="resetForm"
          hide-footer
          centered
        >
          <validation-observer
            ref="addMetricForm"
            v-slot="{ invalid, validate }"
          >
            <b-form
              @submit.prevent="
                validate().then((success) => success && addNewMetric())
              "
            >
              <b-form-group label="Name*" label-for="name">
                <validation-provider
                  name="Name"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input id="name" v-model="newMetric.name" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Description*" label-for="description">
                <validation-provider
                  name="Description"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="description"
                    v-model="newMetric.description"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Target*" label-for="target">
                <validation-provider
                  name="Target"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="target"
                    v-model="newMetric.target"
                    type="number"
                    min="1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Actual*" label-for="actual">
                <validation-provider
                  name="Actual"
                  rules="required|numeric"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="actual"
                    v-model="newMetric.actual"
                    type="number"
                    min="1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Status*" label-for="status">
                <validation-provider
                  name="Status"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-select
                    id="status"
                    v-model="newMetric.status"
                    :options="statusOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Date" label-for="date">
                <validation-provider
                  rules="required"
                  #default="{ errors }"
                  name="date"
                  vid="date"
                >
                  <b-form-datepicker
                    id="date"
                    placeholder="Date"
                    :state="errors.length > 0 ? false : null"
                    v-model="newMetric.date"
                    :max="today"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-button type="submit" variant="primary" :disabled="invalid">
                Add Metric
              </b-button>
            </b-form>
          </validation-observer>
        </b-modal>

        <!--Edit modal-->
        <b-modal
          ref="modalEditMetric"
          id="modal-edit-metric"
          title="Edit Metric"
          hide-footer
          centered
        >
          <validation-observer
            ref="editMetricForm"
            v-slot="{ invalid, validate }"
          >
            <b-form
              @submit.prevent="
                validate().then((success) => success && updateMetric())
              "
            >
              <b-form-group label="Name" label-for="editName">
                <validation-provider
                  name="Name"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-input id="editName" v-model="editMetric.name" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Description" label-for="editDescription">
                <validation-provider
                  name="Description"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-textarea
                    id="description"
                    v-model="editMetric.description"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Target" label-for="editTarget">
                <validation-provider
                  name="Target"
                  rules="required|numeric"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="target"
                    v-model="editMetric.target"
                    type="number"
                    min="1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Actual" label-for="editActual">
                <validation-provider
                  name="Actual"
                  rules="required|numeric"
                  v-slot="{ errors }"
                >
                  <b-form-input
                    id="Actual"
                    v-model="editMetric.actual"
                    type="number"
                    min="1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Status" label-for="editStatus">
                <validation-provider
                  name="Status"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-select
                    id="editStatus"
                    v-model="editMetric.status"
                    :options="statusOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Date" label-for="date">
                <validation-provider
                  rules="required"
                  #default="{ errors }"
                  name="date"
                  vid="date"
                >
                  <b-form-datepicker
                    id="date"
                    placeholder="Date"
                    :state="errors.length > 0 ? false : null"
                    v-model="editMetric.date"
                    :max="today"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-button type="submit" variant="primary" :disabled="invalid">
                Save Changes
              </b-button>
            </b-form>
          </validation-observer>
        </b-modal>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import {
  BTable,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BFormDatepicker,
  BInputGroupAppend,
  BButton,
  BCard,
  BForm,
  BCardBody,
  BDropdown,
  BModal,
  BDropdownItem,
  BSpinner,
  BBadge,
  BFormTextarea,
} from "bootstrap-vue";
import { mapGetters, mapActions } from "vuex";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required, numeric } from "vee-validate/dist/rules";
import { IsAdmin } from "@/@core/utils/validations/permissionCheck";
//import metrics from "../../../store/metrics";

extend("required", {
  ...required,
  message: "This field is required",
});
extend("numeric", {
  ...numeric,
  message: "This field must be a number",
});

export default {
  components: {
    BCard,
    BCardBody,
    BTable,
    BModal,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    BBadge,
    ValidationProvider,
    BSpinner,
    BFormDatepicker,
    BFormTextarea,
  },
  data() {
    return {
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentPage: 1,
      filter: null,
      filters: {
        name: "",
        description: "",
      },
      filterOn: ["name", "description"],
      metrics_fields: [
        { key: "name", label: "Name", sortable: true },
        { key: "description", label: "Description", sortable: true },
        { key: "target", label: "Target", sortable: true },
        { key: "actual", label: "Actual", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "date", label: "Date", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      statusOptions: [
        { value: "ACTIVE", text: "ACTIVE" },
        { value: "INACTIVE", text: "INACTIVE" },
      ],
      newMetric: {
        name: null,
        description: null,
        target: null,
        actual: null,
        status: null,
        date: null,
      },
      editMetric: {
        name: null,
        description: null,
        target: null,
        actual: null,
        status: null,
        date: null,
      },
    };
  },
  computed: {
    ...mapGetters("metricModule", {
      metricslist: "metrics",
      loading: "loading",
    }),
  },
  methods: {
    ...mapActions("metricModule", [
      "getMetricsListAction",
      "removeMetricAction",
      "addMetricAction",
      "updateMetricAction",
    ]),

    formatDate2(date) {
      if (!date) return "-";
      return new Date(date).toISOString().split("T")[0]; // Returns YYYY-MM-DD
    },
    formatDateDDMMMYYYY(dateStr) {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
},
    showAddMetricModal() {
      this.$refs.modalAddMetric.show();
    },

    async addNewMetric() {
      this.loading = true;
      const loader = Swal.fire({
        title: "Processing...",
        text: "Please wait while the Metric is being added",
        allowOutsideClick: () => !Swal.isLoading(),
        showConfirmButton: false,
        showCancelButton: false,
        buttonsStyling: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await this.addMetricAction(this.newMetric);
        loader.close();
        this.$bvModal.hide("modal-add-metric");
        this.success("Metric added successfully.");
        this.getMetricsListAction();
        this.resetForm();
      } catch (error) {
        loader.close();
        //this.error(error.response.data);
        Swal.fire({
          title: "Error!",
          text:
            error.response.data.message ||
            "An error occurred while adding metric.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.loading = false;
      }
    },

    showEditMetricModal(item) {
      this.editMetric = { ...item };
      this.$refs.modalEditMetric.show();
      this.getMetricsListAction();
    },

    async updateMetric() {
      this.loading = true;
      const loader = Swal.fire({
        title: "Processing...",
        text: "Please wait while the Metric is being updated",
        allowOutsideClick: () => !Swal.isLoading(),
        showConfirmButton: false,
        showCancelButton: false,
        buttonsStyling: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await this.updateMetricAction(this.editMetric);
        loader.close();
        this.$bvModal.hide("modal-edit-metric");
        this.success("Metric updated successfully.");
        this.loading = false;
        this.resetForm();
        this.getMetricsListAction();
      } catch (error) {
        loader.close();
        this.error(error.response.data);
        this.loading = false;
        this.getMetricsListAction();
      } finally {
        this.getMetricsListAction();
        this.loading = false;
      }
    },

    async confirmDeleteMetric(id) {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (confirmation.isConfirmed) {
        this.deleteMetric(id);
      } else if (confirmation.dismiss === Swal.DismissReason.cancel) {
        Swal.fire({
          title: "Cancelled",
          text: "Your Metric is safe :)",
          icon: "error",
          customClass: {
            confirmButton: "btn btn-success",
          },
        });
      }
    },

    async deleteMetric(id) {
      const loader = Swal.fire({
        title: "Processing...",
        text: "Please wait while the Metrics is being deleted",
        allowOutsideClick: () => !Swal.isLoading(),
        showConfirmButton: false,
        showCancelButton: false,
        buttonsStyling: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await this.removeMetricAction(id);
        loader.close();
        Swal.fire({
          title: "Deleted!",
          text: "The Metric has been deleted.",
          icon: "success",
          customClass: {
            confirmButton: "btn btn-success",
          },
        });
        this.resetForm();
        this.getMetricsListAction();
      } catch (error) {
        loader.close();
        this.error(error.response.data);
        this.getMetricsListAction();
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.newMetric = {
        name: null,
        description: null,
        target: null,
        actual: null,
        status: null,
        date: null,
      };
      this.editMetric = {
        name: null,
        description: null,
        target: null,
        actual: null,
        status: null,
        date: null,
      };
    },

    success(message) {
      this.$swal({
        title: "Sustainability",
        text: message,
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },
  },
  async mounted() {
    this.getMetricsListAction().then(() => {
      this.totalRows = this.metricslist.length;
    });
  },
  setup() {
    return {
      IsAdmin,
    };
  },
};
</script>
